import React from 'react';
import { useTranslation } from 'react-i18next';
import { changeLanguage, getAvailableLanguages, getCurrentLanguage } from '../i18n';
import { Globe } from 'lucide-react';

/**
 * Language Switcher Component
 * Allows users to switch between English and Spanish
 */
const LanguageSwitcher = ({ variant = 'dropdown', className = '' }) => {
  const { t } = useTranslation();
  const languages = getAvailableLanguages();
  const currentLang = getCurrentLanguage();

  const handleChange = (langCode) => {
    changeLanguage(langCode);
  };

  // Compact variant (for mobile header - just a globe icon toggle)
  if (variant === 'compact') {
    const otherLang = languages.find(l => l.code !== currentLang.substring(0, 2));
    return (
      <button
        onClick={() => handleChange(otherLang?.code || 'en')}
        className={`flex items-center justify-center w-9 h-9 text-stone-500 hover:text-emerald-600 hover:bg-stone-100 rounded-lg transition-colors ${className}`}
        aria-label={`Switch to ${otherLang?.name || 'English'}`}
        data-testid="language-compact-toggle"
        title={`${currentLang.substring(0, 2).toUpperCase()} → ${otherLang?.code.toUpperCase()}`}
      >
        <Globe className="w-4 h-4" />
      </button>
    );
  }

  // Simple toggle variant (for navbar)
  if (variant === 'toggle') {
    const otherLang = languages.find(l => l.code !== currentLang.substring(0, 2));
    return (
      <button
        onClick={() => handleChange(otherLang?.code || 'en')}
        className={`flex items-center gap-2 px-3 py-2 text-sm font-medium text-stone-600 hover:text-emerald-700 hover:bg-emerald-50 rounded-lg transition-colors ${className}`}
        aria-label={t('settings.language')}
        data-testid="language-toggle"
      >
        <Globe className="w-4 h-4" />
        <span>{otherLang?.flag} {otherLang?.code.toUpperCase()}</span>
      </button>
    );
  }

  // Dropdown variant (for settings)
  if (variant === 'dropdown') {
    return (
      <div className={`relative ${className}`}>
        <label className="block text-sm font-medium text-stone-700 mb-2">
          {t('settings.language')}
        </label>
        <select
          value={currentLang.substring(0, 2)}
          onChange={(e) => handleChange(e.target.value)}
          className="w-full h-10 px-3 rounded-lg border border-stone-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
          data-testid="language-select"
        >
          {languages.map((lang) => (
            <option key={lang.code} value={lang.code}>
              {lang.flag} {lang.name}
            </option>
          ))}
        </select>
      </div>
    );
  }

  // Button group variant
  return (
    <div className={`flex gap-1 p-1 bg-stone-100 rounded-lg ${className}`} role="group" aria-label={t('settings.language')}>
      {languages.map((lang) => (
        <button
          key={lang.code}
          onClick={() => handleChange(lang.code)}
          className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
            currentLang.substring(0, 2) === lang.code
              ? 'bg-white text-emerald-700 shadow-sm'
              : 'text-stone-600 hover:text-stone-900'
          }`}
          data-testid={`language-btn-${lang.code}`}
        >
          <span>{lang.flag}</span>
          <span>{lang.code.toUpperCase()}</span>
        </button>
      ))}
    </div>
  );
};

export default LanguageSwitcher;
